import { Injectable } from '@angular/core';
import { UserModelInterfaces } from '../../interfaces/responses/user-model.interfaces';

const APPROVE_ROLES = ['Super Admin', 'Admin', 'Credit Officer'];
const DISBURSE_ROLES = ['Super Admin', 'Disbursement Officer'];
// Roles explicitly denied an action that is otherwise enabled for everyone with product access.
// Any role NOT listed here keeps the action enabled — these are deny-lists, not allow-lists.
const NO_RECORD_PAYMENT_ROLES = ['Business Development', 'Call Center', 'Disbursement Officer'];
const NO_PAYMENT_UPLOAD_ROLES = ['Business Development', 'Call Center'];
const NO_DELETE_REPAYMENT_ROLES = ['Business Development'];
const PIN_RESET_ROLES = ['Super Admin', 'Admin', 'Call Center', 'Customer Service'];

export type Section =
  | 'institutions'
  | 'categories'
  | 'products'
  | 'applications'
  | 'applicationForms'
  | 'adminMgmt'
  | 'apiKeys'
  | 'webhooks';

// Sections each role has any access to (View/Create/CRUD/Limited/Full all count as access;
// omitted from the list means the matrix marks it ✗ or None for that role).
const ROLE_SECTION_ACCESS: Record<string, Section[]> = {
  'Super Admin': ['institutions', 'categories', 'products', 'applications', 'applicationForms', 'adminMgmt', 'apiKeys', 'webhooks'],
  'Admin': ['institutions', 'categories', 'products', 'applications', 'applicationForms', 'adminMgmt', 'apiKeys', 'webhooks'],
  'Credit Officer': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'Disbursement Officer': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'Call Center': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'Customer Service': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'Operations': ['institutions', 'categories', 'products'],
  'Loan Officer': ['institutions', 'categories', 'products'],
  'Finance': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'Business Development': ['institutions', 'categories', 'products', 'applications', 'applicationForms'],
  'HR': ['institutions'],
  'Data Analyst': ['institutions', 'categories', 'products', 'applications'],
  'Data Analyst Admin': ['institutions', 'categories', 'products', 'applications'],
  'Developers': ['apiKeys', 'webhooks'],
};

// Sections each role can create/edit/delete in (vs. read-only access from ROLE_SECTION_ACCESS above).
const ROLE_SECTION_MANAGE: Record<string, Section[]> = {
  'Super Admin': ['institutions', 'categories', 'products', 'applications', 'applicationForms', 'adminMgmt', 'apiKeys', 'webhooks'],
  'Admin': ['institutions', 'categories', 'products', 'applications', 'applicationForms', 'adminMgmt', 'apiKeys', 'webhooks'],
  'Operations': ['products'],
  'Loan Officer': ['products'],
  'Developers': ['apiKeys', 'webhooks'],
};

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  private getRoleNames(): string[] {
    if (typeof window === 'undefined') return [];
    const raw = localStorage.getItem('userInfo');
    if (!raw) return [];
    try {
      const parsed: UserModelInterfaces = JSON.parse(raw);
      return (parsed.user?.roles ?? []).map(role => role.name);
    } catch {
      return [];
    }
  }

  canApprove(): boolean {
    const roles = this.getRoleNames();
    return roles.some(role => APPROVE_ROLES.includes(role));
  }

  canDisburse(): boolean {
    const roles = this.getRoleNames();
    return roles.some(role => DISBURSE_ROLES.includes(role));
  }

  canRecordPayment(): boolean {
    const roles = this.getRoleNames();
    if (roles.length === 0) return false;
    return !roles.some(role => NO_RECORD_PAYMENT_ROLES.includes(role));
  }

  canUploadPayment(): boolean {
    const roles = this.getRoleNames();
    if (roles.length === 0) return false;
    return !roles.some(role => NO_PAYMENT_UPLOAD_ROLES.includes(role));
  }

  canDeleteRepayment(): boolean {
    const roles = this.getRoleNames();
    if (roles.length === 0) return false;
    return !roles.some(role => NO_DELETE_REPAYMENT_ROLES.includes(role));
  }

  canResetPin(): boolean {
    const roles = this.getRoleNames();
    return roles.some(role => PIN_RESET_ROLES.includes(role));
  }

  isSuperAdmin(): boolean {
    return this.getRoleNames().includes('Super Admin');
  }

  canAccess(section: Section): boolean {
    const roles = this.getRoleNames();
    return roles.some(role => ROLE_SECTION_ACCESS[role]?.includes(section));
  }

  canManage(section: Section): boolean {
    const roles = this.getRoleNames();
    return roles.some(role => ROLE_SECTION_MANAGE[role]?.includes(section));
  }
}
